export type UniversalDocumentPackageId = "wr-d";

export type UniversalDocumentPackageTranslationKey =
  | "UniversalDocumentPackage.WRD"
  | "UniversalDocumentPackage.WRD.description";

export type UniversalPackageDocument = {
  id: string;
  name: string;
  status: string;
};

export type UniversalDocumentPackage<
  TDocument extends UniversalPackageDocument,
> = {
  id: UniversalDocumentPackageId;
  labelKey: Extract<
    UniversalDocumentPackageTranslationKey,
    "UniversalDocumentPackage.WRD"
  >;
  descriptionKey: Extract<
    UniversalDocumentPackageTranslationKey,
    "UniversalDocumentPackage.WRD.description"
  >;
  documents: TDocument[];
};

type UniversalDocumentPackageDefinition = {
  id: UniversalDocumentPackageId;
  labelKey: Extract<
    UniversalDocumentPackageTranslationKey,
    "UniversalDocumentPackage.WRD"
  >;
  descriptionKey: Extract<
    UniversalDocumentPackageTranslationKey,
    "UniversalDocumentPackage.WRD.description"
  >;
  matchesDocument: (document: UniversalPackageDocument) => boolean;
};

const UNIVERSAL_DOCUMENT_PACKAGE_DEFINITIONS: readonly UniversalDocumentPackageDefinition[] =
  [
    {
      id: "wr-d",
      labelKey: "UniversalDocumentPackage.WRD",
      descriptionKey: "UniversalDocumentPackage.WRD.description",
      matchesDocument: (document) => /^wr[-_]d[-_]/i.test(document.name),
    },
  ];

export function listUniversalDocumentPackages<
  TDocument extends UniversalPackageDocument,
>(documents: TDocument[]): UniversalDocumentPackage<TDocument>[] {
  return UNIVERSAL_DOCUMENT_PACKAGE_DEFINITIONS.map((definition) => ({
    id: definition.id,
    labelKey: definition.labelKey,
    descriptionKey: definition.descriptionKey,
    documents: documents.filter(
      (document) =>
        document.status === "processed" && definition.matchesDocument(document)
    ),
  })).filter((pkg) => pkg.documents.length > 0);
}

export function getUniversalDocumentPackageById<
  TDocument extends UniversalPackageDocument,
>(
  documents: TDocument[],
  packageId: UniversalDocumentPackageId
): UniversalDocumentPackage<TDocument> | null {
  return (
    listUniversalDocumentPackages(documents).find(
      (pkg) => pkg.id === packageId
    ) ?? null
  );
}

function getUniversalDocumentPackageDefinitionById(
  packageId: UniversalDocumentPackageId
) {
  return (
    UNIVERSAL_DOCUMENT_PACKAGE_DEFINITIONS.find(
      (pkg) => pkg.id === packageId
    ) ?? null
  );
}

export function isUniversalDocumentInPackage(
  document: UniversalPackageDocument,
  packageId: UniversalDocumentPackageId
) {
  return (
    getUniversalDocumentPackageDefinitionById(packageId)?.matchesDocument(
      document
    ) ?? false
  );
}

export function getPackagedUniversalDocumentIdSet<
  TDocument extends UniversalPackageDocument,
>(packages: UniversalDocumentPackage<TDocument>[]) {
  return new Set(packages.flatMap((pkg) => pkg.documents.map((doc) => doc.id)));
}
